/**
 * Safe math expression evaluator for the AI MCP chat.
 *
 * Implements a small recursive-descent parser (no eval / new Function) so the
 * model can safely run arithmetic, statistics and complex operations without
 * executing arbitrary JavaScript.
 *
 * Supported:
 *  - Operators: + - * / % ^ (power), parentheses, unary +/-
 *  - Functions: sqrt, cbrt, abs, round, floor, ceil, trunc, sign,
 *               min, max, pow, log, log10, ln, exp,
 *               sin, cos, tan, asin, acos, atan, atan2,
 *               average/avg/mean, median, sum, product, variance, stddev,
 *               gcd, lcm, factorial, clamp, lerp, percentage
 *  - Constants: pi, e, tau, phi
 *  - Arrays: [1, 2, 3] literals usable with aggregate functions
 */

const CONSTANTS = {
  pi: Math.PI,
  e: Math.E,
  tau: Math.PI * 2,
  phi: (1 + Math.sqrt(5)) / 2,
};

const FUNCTIONS = {
  abs: (x) => Math.abs(x),
  cbrt: (x) => Math.cbrt(x),
  round: (x, p = 0) => {
    const factor = Math.pow(10, Number(p) || 0);
    return Math.round(x * factor) / factor;
  },
  floor: (x) => Math.floor(x),
  ceil: (x) => Math.ceil(x),
  trunc: (x) => Math.trunc(x),
  min: (...args) => Math.min(...flatten(args)),
  max: (...args) => Math.max(...flatten(args)),
  pow: (a, b) => Math.pow(a, b),
  log: (x) => Math.log(x),
  log10: (x) => Math.log10(x),
  exp: (x) => Math.exp(x),
  sin: (x) => Math.sin(x),
  cos: (x) => Math.cos(x),
  tan: (x) => Math.tan(x),
  asin: (x) => Math.asin(x),
  acos: (x) => Math.acos(x),
  atan: (x) => Math.atan(x),
  atan2: (y, x) => Math.atan2(y, x),
  sqrt: (x) => Math.sqrt(x),
  average: (...args) => meanOf(flatten(args)),
  mean: (...args) => meanOf(flatten(args)),
  avg: (...args) => meanOf(flatten(args)),
  median: (...args) => medianOf(flatten(args)),
  sum: (...args) => sumOf(flatten(args)),
  product: (...args) => productOf(flatten(args)),
  variance: (...args) => varianceOf(flatten(args)),
  stddev: (...args) => Math.sqrt(varianceOf(flatten(args))),
  gcd: (...args) => gcdOf(flatten(args)),
  count: (...args) => flatten(args).length,
  factorial: (n) => factorialOf(n),
  lerp: (a, b, t) => a + (b - a) * t,
  percentage: (part, whole) => (part / whole) * 100,
};

function flatten(args) {
  const out = [];
  const walk = (value) => {
    if (Array.isArray(value)) {
      value.forEach(walk);
    } else if (typeof value === "number" && Number.isFinite(value)) {
      out.push(value);
    }
  };
  args.forEach(walk);
  return out;
}

function meanOf(values) {
  if (values.length === 0) {
    throw new Error("Cannot compute mean of an empty set.");
  }
  return sumOf(values) / values.length;
}

function medianOf(values) {
  if (values.length === 0) {
    throw new Error("Cannot compute median of an empty set.");
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) {
    return sorted[mid];
  }
  return (sorted[mid - 1] + sorted[mid]) / 2;
}

function sumOf(values) {
  return values.reduce((acc, v) => acc + v, 0);
}

function productOf(values) {
  if (values.length === 0) {
    throw new Error("Cannot compute product of an empty set.");
  }
  return values.reduce((acc, v) => acc * v, 1);
}

function varianceOf(values) {
  if (values.length < 2) {
    throw new Error("Variance requires at least two values.");
  }
  const mean = meanOf(values);
  return values.reduce((acc, v) => acc + (v - mean) * (v - mean), 0) / (values.length - 1);
}

function gcdOf(values) {
  if (values.length === 0) {
    throw new Error("Cannot compute gcd of an empty set.");
  }
  const gcd2 = (a, b) => {
    a = Math.abs(Math.trunc(a));
    b = Math.abs(Math.trunc(b));
    while (b) {
      [a, b] = [b, a % b];
    }
    return a;
  };
  return values.map((v) => Math.abs(Math.trunc(v))).reduce((acc, v) => gcd2(acc, v));
}

function factorialOf(n) {
  const k = Math.trunc(n);
  if (k < 0) {
    throw new Error("Factorial is not defined for negative numbers.");
  }
  if (k > 170) {
    throw new Error("Factorial is too large to compute.");
  }
  let result = 1;
  for (let i = 2; i <= k; i += 1) {
    result *= i;
  }
  return result;
}

class Tokenizer {
  constructor(input) {
    this.input = String(input || "").trim();
    this.pos = 0;
  }

  peek() {
    return this.input[this.pos];
  }

  next() {
    return this.input[this.pos++];
  }

  skipWhitespace() {
    while (this.pos < this.input.length && /\s/.test(this.input[this.pos])) {
      this.pos += 1;
    }
  }

  readNumber() {
    let start = this.pos;
    while (this.pos < this.input.length && /[0-9.]/.test(this.input[this.pos])) {
      this.pos += 1;
    }
    const raw = this.input.slice(start, this.pos);
    const value = Number(raw);
    if (!Number.isFinite(value)) {
      throw new Error(`Invalid number literal: ${raw}`);
    }
    return { type: "number", value };
  }

  readIdentifier() {
    let start = this.pos;
    while (this.pos < this.input.length && /[a-zA-Z_]/.test(this.input[this.pos])) {
      this.pos += 1;
    }
    return this.input.slice(start, this.pos);
  }

  tokenize() {
    const tokens = [];
    while (this.pos < this.input.length) {
      this.skipWhitespace();
      if (this.pos >= this.input.length) {
        break;
      }
      const ch = this.peek();
      if (/[0-9.]/.test(ch)) {
        tokens.push(this.readNumber());
        continue;
      }
      if (/[a-zA-Z_]/.test(ch)) {
        const name = this.readIdentifier();
        if (name === "pi" || name === "e" || name === "tau" || name === "phi") {
          tokens.push({ type: "constant", name });
        } else {
          tokens.push({ type: "ident", name });
        }
        continue;
      }
      this.next();
      if (ch === "+" || ch === "-" || ch === "*" || ch === "/" || ch === "%" || ch === "^"
        || ch === "(" || ch === ")" || ch === "[" || ch === "]" || ch === ",") {
        tokens.push({ type: "op", value: ch });
        continue;
      }
      throw new Error(`Unexpected character: ${ch}`);
    }
    tokens.push({ type: "eof" });
    return tokens;
  }
}

class Parser {
  constructor(tokens) {
    this.tokens = tokens;
    this.pos = 0;
  }

  peek() {
    return this.tokens[this.pos];
  }

  next() {
    return this.tokens[this.pos++];
  }

  expect(type, value) {
    const token = this.next();
    if (token.type !== type || (value !== undefined && token.value !== value)) {
      throw new Error(`Unexpected token: ${JSON.stringify(token)}`);
    }
    return token;
  }

  parse() {
    const result = this.parseExpression();
    if (this.peek().type !== "eof") {
      throw new Error("Unexpected trailing tokens in expression.");
    }
    return result;
  }

  parseExpression() {
    return this.parseAdditive();
  }

  parseAdditive() {
    let left = this.parseMultiplicative();
    while (this.peek().type === "op" && (this.peek().value === "+" || this.peek().value === "-")) {
      const op = this.next().value;
      const right = this.parseMultiplicative();
      left = op === "+" ? left + right : left - right;
    }
    return left;
  }

  parseMultiplicative() {
    let left = this.parsePower();
    while (this.peek().type === "op" && (this.peek().value === "*" || this.peek().value === "/" || this.peek().value === "%")) {
      const op = this.next().value;
      const right = this.parsePower();
      if (op === "*") {
        left = left * right;
      } else if (op === "/") {
        if (right === 0) {
          throw new Error("Division by zero.");
        }
        left = left / right;
      } else {
        if (right === 0) {
          throw new Error("Modulo by zero.");
        }
        left = left % right;
      }
    }
    return left;
  }

  parsePower() {
    const left = this.parseUnary();
    if (this.peek().type === "op" && this.peek().value === "^") {
      this.next();
      const right = this.parsePower();
      return Math.pow(left, right);
    }
    return left;
  }

  parseUnary() {
    if (this.peek().type === "op" && (this.peek().value === "+" || this.peek().value === "-")) {
      const op = this.next().value;
      const value = this.parseUnary();
      return op === "-" ? -value : value;
    }
    return this.parsePrimary();
  }

  parsePrimary() {
    const token = this.peek();

    if (token.type === "number") {
      this.next();
      return token.value;
    }

    if (token.type === "constant") {
      this.next();
      return CONSTANTS[token.name];
    }

    if (token.type === "op" && token.value === "(") {
      this.next();
      const value = this.parseExpression();
      this.expect("op", ")");
      return value;
    }

    if (token.type === "op" && token.value === "[") {
      return this.parseArray();
    }

    if (token.type === "ident") {
      this.next();
      const name = token.name;
      if (this.peek().type === "op" && this.peek().value === "(") {
        return this.parseFunctionCall(name);
      }
      throw new Error(`Unknown identifier: ${name}`);
    }

    throw new Error(`Unexpected token: ${JSON.stringify(token)}`);
  }

  parseArray() {
    this.expect("op", "[");
    const values = [];
    if (this.peek().type === "op" && this.peek().value === "]") {
      this.next();
      return values;
    }
    values.push(this.parseExpression());
    while (this.peek().type === "op" && this.peek().value === ",") {
      this.next();
      values.push(this.parseExpression());
    }
    this.expect("op", "]");
    return values;
  }

  parseFunctionCall(name) {
    this.expect("op", "(");
    const args = [];
    if (this.peek().type === "op" && this.peek().value === ")") {
      this.next();
    } else {
      args.push(this.parseExpression());
      while (this.peek().type === "op" && this.peek().value === ",") {
        this.next();
        args.push(this.parseExpression());
      }
      this.expect("op", ")");
    }

    const fn = FUNCTIONS[name];
    if (!fn) {
      throw new Error(`Unknown function: ${name}`);
    }
    return fn(...args);
  }
}

/**
 * Evaluate a math expression string.
 * @param {string} expression
 * @returns {number|number[]}
 */
export function evaluateMathExpression(expression) {
  if (typeof expression !== "string" || expression.trim() === "") {
    throw new Error("Expression is required.");
  }
  const tokens = new Tokenizer(expression).tokenize();
  const parser = new Parser(tokens);
  return parser.parse();
}

/**
 * Convenience wrapper that returns a structured result object suitable for
 * the AI tool payload.
 */
export function runMathTool({ expression, operation, numbers }) {
  let result;
  let usedExpression = expression;

  if (typeof usedExpression === "string" && usedExpression.trim() !== "") {
    result = evaluateMathExpression(usedExpression);
  } else if (Array.isArray(numbers) && numbers.length > 0) {
    const op = String(operation || "average").toLowerCase();
    const values = numbers.map((n) => Number(n)).filter((n) => Number.isFinite(n));
    if (values.length === 0) {
      throw new Error("No valid numbers provided.");
    }
    const fn = FUNCTIONS[op] || FUNCTIONS.average;
    result = fn(...values);
    usedExpression = `${op}(${values.join(", ")})`;
  } else {
    throw new Error("Provide either an expression or a numbers array.");
  }

  return {
    expression: usedExpression,
    result,
    result_type: Array.isArray(result) ? "array" : "number",
  };
}

export default {
  evaluateMathExpression,
  runMathTool,
};